import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import FocusLock from "react-focus-lock";
import { useTranslation } from "react-i18next";

import { movementTypeLabel } from "../../../utils/i18nLabels";

function formatDecimal(value, digits = 3) {
  if (value === null || value === undefined || value === "") return "—";
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value);
  return n.toFixed(digits).replace(/\.?0+$/, "");
}

function formatMoney(value) {
  if (value === null || value === undefined || value === "") return "—";
  const n = Number(value);
  return Number.isFinite(n) ? `${n.toFixed(2)} PLN` : String(value);
}

function formatDateOnly(value, language = "en") {
  if (!value) return "—";
  const d = new Date(`${value}T00:00:00`);
  if (Number.isNaN(d.getTime())) return String(value);
  const locale = String(language || "en").startsWith("pl") ? "pl-PL" : "en-GB";
  return d.toLocaleDateString(locale, { year: "numeric", month: "2-digit", day: "2-digit" });
}

function formatDateTime(value, language = "en") {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  const locale = String(language || "en").startsWith("pl") ? "pl-PL" : "en-GB";
  return d.toLocaleString(locale, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function userLabel(user) {
  if (!user) return "—";
  const fullName = [user.first_name, user.last_name].filter(Boolean).join(" ").trim();
  return fullName || user.username || `#${user.id}`;
}

function costEntryModeLabel(t, mode) {
  if (!mode) return "—";
  if (mode === "stock_unit") return t("stock:costing.modeStockUnit");
  if (mode === "package") return t("stock:costing.modePackage");
  if (mode === "total") return t("stock:costing.modeTotal");
  return mode;
}

function signedProductDelta(movement) {
  const delta = Number(movement?.product_quantity_delta);
  if (!Number.isFinite(delta)) return "—";
  if (delta > 0) return `+${formatDecimal(delta, 3)}`;
  if (delta < 0) return `−${formatDecimal(Math.abs(delta), 3)}`;
  return `0`;
}

function DetailField({ label, value, wide = false, mono = false }) {
  return (
    <div className={`stock-audit-detail__field${wide ? " stock-audit-detail__field--wide" : ""}`}>
      <dt>{label}</dt>
      <dd className={mono ? "stock-audit-detail__mono" : ""}>{value ?? "—"}</dd>
    </div>
  );
}

function StockMovementDetailDialog({ movement, open, onClose, canCorrect = false, onCorrect }) {
  const { t, i18n } = useTranslation(["common", "stock"]);
  const language = i18n.resolvedLanguage || i18n.language;

  useEffect(() => {
    if (!open) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose?.();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open, onClose]);

  if (!open || !movement) return null;

  const unit = movement.stock_unit_code || movement.product?.stock_unit_code || movement.product?.stockUnit?.symbol || "";
  const allocations = Array.isArray(movement.batchAllocations) ? movement.batchAllocations : [];
  const traceBatchAllocation = movement.trace_batch_id
    ? allocations.find((allocation) => String(allocation.batch_id) === String(movement.trace_batch_id))
    : null;
  const traceBatchLabel = traceBatchAllocation?.batch?.batch_number || (movement.trace_batch_id ? `#${movement.trace_batch_id}` : "");
  const packageContent = movement.package_content_quantity_snapshot && movement.package_content_unit_code_snapshot
    ? `${formatDecimal(movement.package_content_quantity_snapshot, 3)} ${movement.package_content_unit_code_snapshot}`
    : "—";
  const postedBy = userLabel(movement.user);
  const roleSuffix = movement.user?.role
    ? ` · ${t(`common:roles.${movement.user.role}`, { defaultValue: movement.user.role })}`
    : "";
  const valueAdjustment = movement.valuationAdjustment || movement.valuation_adjustment || null;
  const valueAdjustmentLines = Array.isArray(valueAdjustment?.lines) ? valueAdjustment.lines : [];
  const valueAdjustmentKindLabel = valueAdjustment?.adjustment_kind === "historical_correction"
    ? t("stock:valueAdjustment.detailHistorical")
    : t("stock:valueAdjustment.detailCurrent");

  return createPortal(
    <div
      className="stock-dialog-backdrop"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose?.();
      }}
    >
      <FocusLock returnFocus>
        <div
          className="stock-audit-dialog"
          role="dialog"
          aria-modal="true"
          aria-labelledby="stock-movement-detail-title"
        >
          <header className="stock-audit-dialog__header">
            <div>
              <span className="stock-dialog__eyebrow">{t("stock:audit.detailEyebrow")}</span>
              <h2 id="stock-movement-detail-title">
                {t("stock:audit.movementTitle", { id: movement.id })}
              </h2>
              <p>{movementTypeLabel(t, movement.movement_type)}</p>
            </div>
            <button
              type="button"
              className="stock-dialog__close"
              onClick={onClose}
              aria-label={t("common:actions.close")}
              title={t("common:actions.close")}
            >
              ×
            </button>
          </header>

          <div className="stock-audit-dialog__body">
            <section className="stock-audit-detail__section">
              <h3>{t("stock:integration.canonicalSource")}</h3>
              <p>{t("stock:integration.effectiveAuditNotice")}</p>
              {movement.source_context ? <dl className="stock-audit-detail__grid">
                <DetailField label={t("stock:integration.sourceModule")} value={t(`stock:integration.modules.${movement.source_context.module}`, { defaultValue: movement.source_context.module })} />
                <DetailField label={t("stock:integration.documentId")} value={movement.source_context.document_id} />
                <DetailField label={t("stock:integration.lineId")} value={movement.source_context.line_id} />
                <DetailField label={t("stock:integration.dateSource")} value={movement.source_context.date_source} />
                <DetailField label={t("stock:integration.sourceIntegrity")} value={movement.source_context.integrity} />
                {movement.source_context.related?.preparation_id && <DetailField label={t("stock:integration.preparationId")} value={movement.source_context.related.preparation_id} />}
                {movement.source_context.related?.consumption_entry_id && <DetailField label={t("stock:integration.consumptionEntryId")} value={movement.source_context.related.consumption_entry_id} />}
              </dl> : <p>{t("stock:integration.legacySourceUnknown")}</p>}
              {movement.read_context && <small className="stock-read-context">{t("stock:integration.readSnapshot")}: {movement.read_context.request_started_at} · {movement.read_context.id}</small>}
              {movement.correction_policy && !movement.correction_policy.allowed && (
                <div className="stock-correction-policy-note">
                  {t(`stock:movementCorrection.policy.${movement.correction_policy.reason_code}`, {
                    defaultValue: t("stock:movementCorrection.policy.notAllowed"),
                    module: movement.correction_policy.source_module || "unknown",
                  })}
                </div>
              )}
            </section>

            <section className="stock-audit-detail__summary">
              <div>
                <span>{t("stock:audit.businessDate")}</span>
                <strong>{formatDateOnly(movement.business_date, language)}</strong>
              </div>
              <div>
                <span>{t("stock:audit.registeredAt")}</span>
                <strong>{formatDateTime(movement.createdAt, language)}</strong>
              </div>
              <div>
                <span>{t("stock:audit.quantity")}</span>
                <strong>{formatDecimal(movement.quantity, 6)} {unit}</strong>
              </div>
              <div>
                <span>{t("stock:audit.productStockAfter")}</span>
                <strong>{movement.product_quantity_after == null ? "—" : `${formatDecimal(movement.product_quantity_after, 3)} ${unit}`}</strong>
              </div>
              <div>
                <span>{t("stock:audit.value")}</span>
                <strong>{formatMoney(movement.total_cost_net)}</strong>
              </div>
            </section>

            {valueAdjustment && (
              <section className="stock-audit-detail__section stock-value-adjustment-detail">
                <h3>{t("stock:valueAdjustment.detailTitle")}</h3>
                <p>{valueAdjustment.adjustment_kind === "historical_correction"
                  ? t("stock:valueAdjustment.detailHistoricalHelp")
                  : t("stock:valueAdjustment.detailCurrentHelp")}</p>
                <dl className="stock-audit-detail__grid">
                  <DetailField label={t("stock:valueAdjustment.adjustmentKind")} value={valueAdjustmentKindLabel} />
                  <DetailField label={t("stock:valueAdjustment.valueDelta")} value={formatMoney(valueAdjustment.value_delta_net ?? movement.total_cost_net)} />
                  <DetailField label={t("stock:valueAdjustment.inputMode")} value={t(`stock:valueAdjustment.modes.${valueAdjustment.input_mode}.label`, { defaultValue: valueAdjustment.input_mode })} />
                  <DetailField label={t("stock:valueAdjustment.inputValue")} value={valueAdjustment.input_value ?? "—"} />
                  <DetailField label={t("stock:valueAdjustment.targetValue")} value={formatMoney(valueAdjustment.target_value_net)} />
                  <DetailField label={t("stock:valueAdjustment.targetAverage")} value={valueAdjustment.target_average_cost_net == null ? "—" : `${formatDecimal(valueAdjustment.target_average_cost_net, 6)} PLN/${unit}`} />
                  {valueAdjustment.corrected_movement_id && <DetailField label={t("stock:valueAdjustment.correctedMovement")} value={`#${valueAdjustment.corrected_movement_id}`} mono />}
                </dl>
                {valueAdjustmentLines.length > 0 && (
                  <>
                    <h4>{t("stock:valueAdjustment.lotLines")}</h4>
                    <div className="stock-table-wrap">
                      <table className="stock-table">
                        <thead><tr><th>LOT</th><th className="num">{t("stock:valueAdjustment.lotValueDelta")}</th></tr></thead>
                        <tbody>{valueAdjustmentLines.map((line) => (
                          <tr key={line.id || line.batch_id}>
                            <td>{line.batch?.batch_number || `#${line.batch_id}`}</td>
                            <td className="num">{formatMoney(line.value_delta_net)}</td>
                          </tr>
                        ))}</tbody>
                      </table>
                    </div>
                  </>
                )}
              </section>
            )}

            <section className="stock-audit-detail__section">
              <h3>{t("stock:audit.sections.movement")}</h3>
              <dl className="stock-audit-detail__grid">
                <DetailField label={t("stock:audit.movementId")} value={movement.id} mono />
                {movement.reversal_of_movement_id && <DetailField label={t("stock:postingSafety.originalMovement")} value={`#${movement.reversal_of_movement_id}`} mono />}
                <DetailField label={t("stock:audit.type")} value={movementTypeLabel(t, movement.movement_type)} />
                <DetailField label={t("stock:audit.product")} value={movement.product?.name_public || "—"} />
                <DetailField label={t("stock:audit.productCode")} value={movement.product?.sku || movement.product?.barcode || "—"} />
                <DetailField label={t("stock:audit.fromLocation")} value={movement.fromLocation?.name || "—"} />
                <DetailField label={t("stock:audit.toLocation")} value={movement.toLocation?.name || "—"} />
                <DetailField label={t("stock:audit.postedBy")} value={`${postedBy}${roleSuffix}`} />
                <DetailField label={t("stock:audit.stockUnit")} value={unit || "—"} mono />
                <DetailField
                  label={t("stock:audit.productStockChange")}
                  value={`${signedProductDelta(movement)}${movement.product_quantity_delta == null ? "" : ` ${unit}`}`}
                />
                <DetailField
                  label={t("stock:audit.productStockAfter")}
                  value={movement.product_quantity_after == null ? "—" : `${formatDecimal(movement.product_quantity_after, 3)} ${unit}`}
                />
                {movement.trace_batch_id ? (
                  <DetailField
                    label={t("stock:audit.traceBatchStockAfter", { batch: traceBatchLabel })}
                    value={movement.trace_batch_quantity_after == null ? "—" : `${formatDecimal(movement.trace_batch_quantity_after, 3)} ${unit}`}
                  />
                ) : null}
              </dl>
            </section>

            <section className="stock-audit-detail__section">
              <h3>{t("stock:audit.sections.valuation")}</h3>
              <dl className="stock-audit-detail__grid">
                <DetailField
                  label={t("stock:audit.unitCost")}
                  value={movement.unit_cost_net == null ? "—" : `${formatDecimal(movement.unit_cost_net, 4)} PLN/${unit || "unit"}`}
                />
                <DetailField label={t("stock:audit.totalCost")} value={formatMoney(movement.total_cost_net)} />
                <DetailField label={t("stock:unifiedCore.rounding")} value={formatMoney(movement.valuation_rounding_adjustment_net)} />
                <DetailField label={t("stock:unifiedCore.inputMode")} value={t(`stock:unifiedCore.inputModes.${movement.valuation_input_mode || "legacy"}`, { defaultValue: movement.valuation_input_mode || "—" })} />
                <DetailField
                  label={t("stock:audit.valuationMethod")}
                  value={t(`stock:costing.valuationMethods.${movement.valuation_method || "unknown"}`)}
                />
                <DetailField
                  label={t("stock:audit.averageCostAfter")}
                  value={movement.average_cost_after_net == null ? "—" : `${formatDecimal(movement.average_cost_after_net, 4)} PLN/${unit || "unit"}`}
                />
                <DetailField label={t("stock:audit.costEntryMode")} value={costEntryModeLabel(t, movement.cost_entry_mode)} />
                <DetailField label={t("stock:audit.packageUnitCost")} value={formatMoney(movement.package_unit_cost_net)} />
                <DetailField label={t("stock:audit.packageTotalCost")} value={formatMoney(movement.package_total_cost_net)} />
              </dl>
            </section>

            <p>{t("stock:unifiedCore.roundingHelp")}</p>

            {Array.isArray(movement.valuationRevisions) && movement.valuationRevisions.length > 0 && (
              <section className="stock-audit-detail__section">
                <details>
                  <summary>{t("stock:valuationAudit.title", { count: movement.valuationRevisions.length })}</summary>
                  <p>{t("stock:valuationAudit.explanation")}</p>
                  {movement.valuationRevisions.map((revision) => (
                    <div key={revision.id} className="stock-audit-detail__section">
                      <h4>{t("stock:valuationAudit.revision", { id: revision.id, trigger: revision.trigger_movement_id })}</h4>
                      <p>{formatDateTime(revision.createdAt, language)} · {t("stock:valuationAudit.actor", { id: revision.user_id || "—" })}</p>
                      <dl className="stock-audit-detail__grid">
                        <DetailField label={t("stock:valuationAudit.unitCost")} value={`${revision.before_json?.unit_cost_net ?? "—"} → ${revision.after_json?.unit_cost_net ?? "—"} PLN/${unit}`} />
                        <DetailField label={t("stock:valuationAudit.value")} value={`${formatMoney(revision.before_json?.total_cost_net)} → ${formatMoney(revision.after_json?.total_cost_net)}`} />
                        <DetailField label={t("stock:valuationAudit.average")} value={`${revision.before_json?.average_cost_after_net ?? "—"} → ${revision.after_json?.average_cost_after_net ?? "—"} PLN/${unit}`} />
                        <DetailField label={t("stock:valuationAudit.date")} value={formatDateOnly(revision.business_date, language)} />
                        <DetailField label={t("stock:unifiedCore.revisionType")} value={t(`stock:unifiedCore.revisionTypes.${revision.revision_type}`, { defaultValue: revision.revision_type })} />
                        <DetailField label={t("stock:unifiedCore.rounding")} value={`${formatMoney(revision.before_json?.valuation_rounding_adjustment_net)} → ${formatMoney(revision.after_json?.valuation_rounding_adjustment_net)}`} />
                      </dl>
                      <details><summary>{t("stock:valuationAudit.fullSnapshot")}</summary><pre style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>{JSON.stringify({ before: revision.before_json, after: revision.after_json, context: revision.context_json, algorithm: revision.algorithm_version }, null, 2)}</pre></details>
                    </div>
                  ))}
                </details>
              </section>
            )}

            {Array.isArray(movement.corrections) && movement.corrections.length > 0 && (
              <section className="stock-audit-detail__section">
                <details>
                  <summary>{t("stock:movementCorrection.historyTitle", { count: movement.corrections.length })}</summary>
                  <p>{t("stock:movementCorrection.historyHelp")}</p>
                  {movement.corrections.map((correction) => (
                    <div key={correction.id} className="stock-correction-history-item">
                      <h4>{t("stock:movementCorrection.historyRevision", { number: correction.correction_no })}</h4>
                      <p>{formatDateTime(correction.createdAt, language)} · {userLabel(correction.createdBy)}</p>
                      <dl className="stock-audit-detail__grid">
                        <DetailField label={t("stock:movementCorrection.quantity")} value={`${correction.before_json?.quantity ?? "—"} → ${correction.after_json?.quantity ?? "—"} ${unit}`} />
                        <DetailField label={t("stock:movementCorrection.movementValue")} value={`${formatMoney(correction.before_json?.total_cost_net)} → ${formatMoney(correction.after_json?.total_cost_net)}`} />
                        <DetailField label={t("stock:movementCorrection.unitCost")} value={`${correction.before_json?.unit_cost_net ?? "—"} → ${correction.after_json?.unit_cost_net ?? "—"} PLN/${unit}`} />
                        <DetailField label={t("stock:movementCorrection.reason")} value={correction.reason} wide />
                        <DetailField label={t("stock:movementCorrection.comment")} value={correction.comment} wide />
                      </dl>
                      <details><summary>{t("stock:valuationAudit.fullSnapshot")}</summary><pre style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>{JSON.stringify({ before: correction.before_json, after: correction.after_json, impact: correction.impact_json, algorithm: correction.algorithm_version }, null, 2)}</pre></details>
                    </div>
                  ))}
                </details>
              </section>
            )}

            {(movement.package_quantity != null || movement.package_label_snapshot || movement.package_content_quantity_snapshot != null) && (
              <section className="stock-audit-detail__section">
                <h3>{t("stock:audit.sections.package")}</h3>
                <dl className="stock-audit-detail__grid">
                  <DetailField label={t("stock:audit.packageQuantity")} value={formatDecimal(movement.package_quantity, 3)} />
                  <DetailField label={t("stock:audit.packageLabel")} value={movement.package_label_snapshot || "—"} />
                  <DetailField label={t("stock:audit.packageContent")} value={packageContent} />
                  <DetailField
                    label={t("stock:audit.stockPerPackage")}
                    value={movement.package_stock_quantity_per_package == null
                      ? "—"
                      : `${formatDecimal(movement.package_stock_quantity_per_package, 3)} ${unit}`}
                  />
                </dl>
              </section>
            )}

            <section className="stock-audit-detail__section">
              <h3>{t("stock:audit.sections.batches")}</h3>
              {allocations.length === 0 ? (
                <p className="stock-audit-detail__empty">{t("stock:audit.noBatchAllocations")}</p>
              ) : (
                <div className="stock-table-wrap stock-audit-detail__batch-table-wrap">
                  <table className="stock-table stock-audit-detail__batch-table">
                    <thead>
                      <tr>
                        <th>{t("stock:audit.batchNumber")}</th>
                        <th>{t("stock:audit.expiryDate")}</th>
                        <th className="num">{t("stock:audit.batchQuantity")}</th>
                        <th className="num">{t("stock:audit.batchUnitCost")}</th>
                        <th className="num">{t("stock:audit.batchValue")}</th>
                        <th className="num">{t("stock:unifiedCore.rounding")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {allocations.map((allocation) => (
                        <tr key={allocation.id || `${allocation.batch_id}-${allocation.quantity}`}>
                          <td>{allocation.batch?.batch_number || `#${allocation.batch_id}`}</td>
                          <td>{formatDateOnly(allocation.batch?.expiry_date, language)}</td>
                          <td className="num">{formatDecimal(allocation.quantity, 6)} {allocation.stock_unit_code || unit}</td>
                          <td className="num">
                            {allocation.unit_cost_net == null ? "—" : `${formatDecimal(allocation.unit_cost_net, 4)} PLN/${allocation.stock_unit_code || unit || "unit"}`}
                          </td>
                          <td className="num">{formatMoney(allocation.total_cost_net)}</td>
                          <td className="num">{formatMoney(allocation.valuation_rounding_adjustment_net)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>

            <section className="stock-audit-detail__section">
              <h3>{t("stock:audit.sections.audit")}</h3>
              <dl className="stock-audit-detail__grid">
                <DetailField label={t("stock:audit.referenceType")} value={movement.reference_type || "—"} mono />
                <DetailField label={t("stock:audit.referenceId")} value={movement.reference_id || "—"} mono />
                <DetailField label={t("stock:audit.reason")} value={movement.reason || "—"} wide />
                <DetailField label={t("stock:audit.comment")} value={movement.comment || "—"} wide />
              </dl>
            </section>
          </div>

          <footer className="stock-audit-dialog__footer">
            <button type="button" className="stock-btn stock-btn--secondary" onClick={onClose}>
              {t("common:actions.close")}
            </button>
            {canCorrect && movement.correction_policy?.allowed && (
              <button type="button" className="stock-btn stock-btn--primary" onClick={() => onCorrect?.(movement)}>
                {t("stock:movementCorrection.action")}
              </button>
            )}
          </footer>
        </div>
      </FocusLock>
    </div>,
    document.body
  );
}

export default StockMovementDetailDialog;

